import { createSystem, defaultConfig, defineConfig, defineRecipe } from "@chakra-ui/react"

const buttonRecipe = defineRecipe({
    className: "bm-button",
    base: {
        fontWeight: "600",
        borderRadius: "md",
        transition: "all 0.2s",
    },
    variants: {
        size: {
            sm: { h: "32px", px: "12px", fontSize: "sm" },
            md: { h: "40px", px: "16px", fontSize: "md" },
            lg: { h: "48px", px: "20px", fontSize: "md" },
        },
        variant: {
            solid: {
                bg: "colorPalette.500",
                color: "white",
                _hover: { bg: "colorPalette.600", transform: "translateY(-2px)", boxShadow: "md" },
                _active: { bg: "colorPalette.700", transform: "translateY(0)" },
            },
            outline: {
                borderWidth: "2px",
                borderColor: { base: "colorPalette.500", _dark: "colorPalette.300" },
                color: { base: "colorPalette.700", _dark: "colorPalette.100" },
                _hover: {
                    bg: { base: "colorPalette.50", _dark: "whiteAlpha.100" },
                    transform: "translateY(-2px)",
                },
                _active: {
                    bg: { base: "colorPalette.100", _dark: "whiteAlpha.200" },
                    transform: "translateY(0)",
                },
            },
            ghost: {
                color: { base: "neutral.700", _dark: "neutral.100" },
                _hover: { bg: { base: "neutral.100", _dark: "whiteAlpha.100" } },
                _active: { bg: { base: "neutral.200", _dark: "whiteAlpha.200" } },
            },
        },
    },
    defaultVariants: {
        size: "md",
        variant: "solid",
    },
})

const config = defineConfig({
    theme: {
        tokens: {
            colors: {
                barberBlue: {
                    50: { value: "#F0F7FF" },
                    100: { value: "#D6EBFF" },
                    200: { value: "#ADDBFF" },
                    300: { value: "#75C2FF" },
                    400: { value: "#3DA9FF" },
                    500: { value: "#0088FF" },
                    600: { value: "#006DD6" },
                    700: { value: "#0052AD" },
                    800: { value: "#003875" },
                    900: { value: "#00203D" },
                },
                barberRed: {
                    50: { value: "#FFF5F5" },
                    100: { value: "#FFE0E0" },
                    200: { value: "#FFC2C2" },
                    300: { value: "#FF9999" },
                    400: { value: "#FF6B6B" },
                    500: { value: "#FF3D3D" },
                    600: { value: "#E61F1F" },
                    700: { value: "#CC0000" },
                    800: { value: "#990000" },
                    900: { value: "#660000" },
                },
                neutral: {
                    50: { value: "#FAFAFA" },
                    100: { value: "#F5F5F5" },
                    200: { value: "#E5E5E5" },
                    300: { value: "#D4D4D4" },
                    400: { value: "#A3A3A3" },
                    500: { value: "#737373" },
                    600: { value: "#525252" },
                    700: { value: "#404040" },
                    800: { value: "#262626" },
                    900: { value: "#171717" },
                },
                success: {
                    50: { value: "#F0FDF4" },
                    100: { value: "#DCFCE7" },
                    200: { value: "#BBF7D0" },
                    300: { value: "#86EFAC" },
                    400: { value: "#4ADE80" },
                    500: { value: "#22C55E" },
                    600: { value: "#16A34A" },
                    700: { value: "#15803D" },
                    800: { value: "#166534" },
                    900: { value: "#14532D" },
                },
                warning: {
                    50: { value: "#FFFBEB" },
                    100: { value: "#FEF3C7" },
                    200: { value: "#FDE68A" },
                    300: { value: "#FCD34D" },
                    400: { value: "#FBBF24" },
                    500: { value: "#F59E0B" },
                    600: { value: "#D97706" },
                    700: { value: "#B45309" },
                    800: { value: "#92400E" },
                    900: { value: "#78350F" },
                },
                // Priority Pass accent (per design_handoff_waitlist/bm.css)
                priority: {
                    50: { value: "#F3F0FF" },
                    100: { value: "#E9E2FF" },
                    200: { value: "#D4C5FF" },
                    300: { value: "#B49DFF" },
                    400: { value: "#9577FF" },
                    500: { value: "#7C5CFF" },
                    600: { value: "#6D28D9" },
                    700: { value: "#5B21B6" },
                    800: { value: "#4C1D95" },
                    900: { value: "#2E1065" },
                },
            },
            fonts: {
                heading: { value: '"Montserrat", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
                body: { value: '"Inter", -apple-system, BlinkMacSystemFont, "Segoe UI", sans-serif' },
                mono: { value: '"Space Mono", monospace' },
            },
            fontSizes: {
                xs: { value: "0.75rem" },
                sm: { value: "0.875rem" },
                md: { value: "1rem" },
                lg: { value: "1.125rem" },
                xl: { value: "1.25rem" },
                "2xl": { value: "1.5rem" },
                "3xl": { value: "2rem" },
                "4xl": { value: "2.5rem" },
                "5xl": { value: "3rem" },
                "6xl": { value: "4rem" },
            },
            // Radii match bm.css exactly: --r-sm 8 / --r-md 12 / --r-lg 16 /
            // --r-xl 20 / --r-2xl 24 / --r-full 9999. These are the design's
            // baseline corner radii — changing them ripples through every card,
            // button and chip.
            radii: {
                sm: { value: "8px" },
                md: { value: "12px" },
                lg: { value: "16px" },
                xl: { value: "20px" },
                "2xl": { value: "24px" },
                full: { value: "9999px" },
            },
            // Shadows match bm.css. The accent shadows are intentional brand
            // hits (used on primary CTAs + tile icons).
            shadows: {
                xs: { value: "0 1px 2px rgba(0, 0, 0, 0.05)" },
                sm: { value: "0 1px 3px rgba(0, 0, 0, 0.05)" },
                md: { value: "0 4px 12px rgba(0, 0, 0, 0.08)" },
                lg: { value: "0 8px 24px rgba(0, 0, 0, 0.10)" },
                xl: { value: "0 16px 48px rgba(0, 0, 0, 0.12)" },
                "2xl": { value: "0 24px 64px rgba(0, 0, 0, 0.15)" },
                blue: { value: "0 4px 12px rgba(0, 136, 255, 0.30)" },
                blueLg: { value: "0 8px 24px rgba(0, 136, 255, 0.40)" },
                red: { value: "0 4px 12px rgba(255, 61, 61, 0.30)" },
            },
        },
        semanticTokens: {
            colors: {
                // Dark-mode values now match `Barbermate-8/bm.css` cool blue-grey
                // ramp instead of the near-black ramp the app had before.
                // Light values unchanged.
                "bg.app": {
                    value: { base: "{colors.neutral.50}", _dark: "#11161f" },
                },
                "bg.surface": {
                    value: { base: "white", _dark: "#28324a" },
                },
                "bg.subtle": {
                    value: { base: "{colors.neutral.100}", _dark: "#1b2230" },
                },
                // Dark text inverts to the design's neutral-900 dark value.
                "fg.default": {
                    value: { base: "{colors.neutral.700}", _dark: "#f5f7fb" },
                },
                "fg.muted": {
                    value: { base: "{colors.neutral.500}", _dark: "#909bb4" },
                },
                "border.default": {
                    value: { base: "{colors.neutral.200}", _dark: "#33405b" },
                },
                // Override Chakra's built-in border token so Input/Select/Textarea
                // outlines stay visible against dark modal backgrounds.
                border: {
                    value: { base: "{colors.neutral.200}", _dark: "#33405b" },
                },
                // ─── Shop-overrideable accent ────────────────────────────
                // Reads from --accent (set by applyAccent() in Booking.tsx
                // and by the Settings live-preview postMessage). Default
                // falls back to brand blue so the rest of the app is
                // unaffected when no shop accent is active.
                "accent.solid": {
                    value: "var(--accent, #0088FF)",
                },
                "accent.subtle": {
                    value: "var(--accent-50, #F0F7FF)",
                },
                "accent.contrast": {
                    value: "white",
                },
            },
        },
        // ─── Type-role textStyles ────────────────────────────────────
        // Match the `--display / --h1..h4 / --body / --body-sm / --label /
        // --eyebrow / --btn` roles in design's colors_and_type.css. Consumers
        // use `textStyle="eyebrow"` etc. on Chakra Text / Box.
        textStyles: {
            display: {
                value: {
                    fontFamily: "heading",
                    fontWeight: "800",
                    lineHeight: "1.05",
                    letterSpacing: "-0.02em",
                },
            },
            h1: {
                value: {
                    fontFamily: "heading",
                    fontWeight: "700",
                    fontSize: "34px",
                    lineHeight: "1.1",
                    letterSpacing: "-0.01em",
                },
            },
            h2: {
                value: {
                    fontFamily: "heading",
                    fontWeight: "700",
                    fontSize: "24px",
                    lineHeight: "1.2",
                    letterSpacing: "-0.01em",
                },
            },
            h3: {
                value: {
                    fontFamily: "heading",
                    fontWeight: "700",
                    fontSize: "18px",
                    lineHeight: "1.3",
                },
            },
            h4: {
                value: {
                    fontFamily: "heading",
                    fontWeight: "600",
                    fontSize: "15px",
                    lineHeight: "1.4",
                },
            },
            bodyLg: {
                value: {
                    fontFamily: "body",
                    fontWeight: "400",
                    fontSize: "18px",
                    lineHeight: "1.6",
                },
            },
            body: {
                value: {
                    fontFamily: "body",
                    fontWeight: "400",
                    fontSize: "15px",
                    lineHeight: "1.6",
                },
            },
            bodySm: {
                value: {
                    fontFamily: "body",
                    fontWeight: "400",
                    fontSize: "13px",
                    lineHeight: "1.5",
                },
            },
            label: {
                value: {
                    fontFamily: "body",
                    fontWeight: "600",
                    fontSize: "13px",
                    lineHeight: "1.4",
                },
            },
            eyebrow: {
                value: {
                    fontFamily: "mono",
                    fontWeight: "700",
                    fontSize: "11px",
                    lineHeight: "1",
                    letterSpacing: "0.2em",
                    textTransform: "uppercase",
                },
            },
            btnMono: {
                value: {
                    fontFamily: "mono",
                    fontWeight: "700",
                    fontSize: "12px",
                    lineHeight: "1",
                    letterSpacing: "0.14em",
                    textTransform: "uppercase",
                },
            },
        },
        recipes: {
            button: buttonRecipe,
            heading: {
                base: {
                    fontFamily: "heading",
                    fontWeight: "700",
                    letterSpacing: "-0.01em",
                },
            },
        },
    },
    globalCss: {
        body: {
            bg: "bg.app",
            color: "fg.default",
        },
        "html, body": {
            margin: 0,
            padding: 0,
            width: "100%",
            height: "100%",
            boxSizing: "border-box",
        },
        "#root": {
            width: "100%",
            height: "100%",
            maxWidth: "100%",
            margin: 0,
            padding: 0,
            textAlign: "left",
        },
        // Placeholders read as a muted gray, clearly distinct from real typed
        // text (fg.default = neutral.700). neutral.400 in light, a dimmed grey
        // in dark. opacity:1 stops Firefox from fading it further.
        "input::placeholder, textarea::placeholder": {
            color: { base: "neutral.400", _dark: "neutral.500" },
            opacity: 1,
        },
    },
})

export const system = createSystem(defaultConfig, config)
